import React, { useMemo } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, TableAction } from "@shared/types/table";
import { DEFAULT_PAGE_NUMBER, PAGE_SIZE } from "@shared/utils/constants";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useCustomFilters } from "@features/cx/shared/utils/filters";
import { useNavigate } from "react-router-dom";
import { ConsumerOnboarding } from "../../types";
import {
  useConsumerList,
  useConsumerListHistory,
  useConsumerOnboardingHistoryExport,
} from "../../hooks";
import { ConsumerOnboardingFilterPanel } from "../filters";

const columns: TableColumn<ConsumerOnboarding>[] = [
  { key: "consumerNo", header: "Account No", type: "text" },
  {
    key: "firstName",
    header: "Applicant",
    type: "custom",
    render: (_: any, row: any) => (
      <div>
        <div className="font-semibold">
          {row.firstName} {row.lastName}
        </div>
        <div className="text-xs text-muted-foreground">{row.email}</div>
      </div>
    ),
  },
  { key: "createdDate", header: "Submission Date", type: "text" },
  {
    key: "statusDisplay",
    header: "Status",
    type: "badge",
    badgeConfig: {
      getVariant: (value: string) =>
        value === "DRAFT STATE" ? "destructive" : "default",
    },
  },
];

export const HistoryList: React.FC = () => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();

  const actions: TableAction<ConsumerOnboarding>[] = [
    {
      label: "View Details",
      onClick: (row) =>
        navigate(`/cx/consumer-onboarding/view/${row.id}/${row.statusDisplay}`),
    },
    // {
    //   label: 'Edit Application',
    //   onClick: (row) => {navigate(`/cx/consumer-management/manual/edit/${row.id}`)},
    //   hidden: (row) => row.statusDisplay === 'DRAFT STATE'
    // },
    {
      label: "Create Service Request",
      onClick: (row) => {
        navigate(`/cx/services/create/?consumerId=${row.id}`);
      },
      hidden: (row) => row.statusDisplay !== "DRAFT STATE",
    },
  ];

  // Memoize the configuration to prevent infinite re-renders
  const filterConfig = useMemo(
    () => ({
      paramNames: {
        status: "status",
        category: "category",
        search: "search_data",
        page: "page",
      },
      defaults: {
        page: DEFAULT_PAGE_NUMBER,
        limit: PAGE_SIZE,
        status: [],
        category: "ALL_CATEGORIES",
        search: "",
      },
      parsers: {
        status: (value: string) => value ? value.split(",").map(Number) : [],
        category: (value: string) => value,
        search: (value: string) => value.trim(),
      },
      serializers: {
        status: (value: number[]) => value?.length ? value.join(",") : "",
        category: (value: string) => value,
        search: (value: string) => value.trim(),
      },
    }),
    [],
  );

  const {
    filters,
    page,
    handleApplyFilters,
    handleResetFilters,
    handlePageChange,
    handleSearch,
  } = useCustomFilters(filterConfig, Number(remoteUtilityId));

  const { data } = useConsumerListHistory({
    page: filters.page,
    limit: PAGE_SIZE,
    searchData: filters.search_data,
    status: filters.status?.join(",") || "",
    remoteUtilityId: Number(remoteUtilityId),
  });

  const exportParams = useMemo(() => {
    return {
      remoteUtilityId: Number(remoteUtilityId),
      searchData: filters.search_data,
      status: filters.status?.join(",") || "",
      page: filters.page,
      limit: PAGE_SIZE,
    };
  }, [filters, remoteUtilityId]);

  const { exportCSV, isExporting } =
    useConsumerOnboardingHistoryExport(exportParams);

  // 4. Add the handleExport function after your other handlers
  const handleExport = () => {
    exportCSV();
  };

  // Transform API data to match the table structure
  const transformedData: ConsumerOnboarding[] =
    data?.results?.map((item: ConsumerOnboarding) => ({
      id: item.id,
      email: item.email,
      category: item.category,
      consumerNo: item.consumerNo,
      status: item.status,
      statusDisplay: item.statusDisplay,
      createdDate: item.createdDate,
      firstName: item.firstName,
      lastName: item.lastName,
      subCategory: item.subCategory,
    })) || [];

  const totalItems = data?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  return (
    <>
      <GenericTable
        data={transformedData}
        columns={columns}
        rowKey="id"
        searchPlaceholder="Search by name, email, or ID..."
        searchParamName="query"
        searchDebounceMs={300}
        onSearch={handleSearch}
        pagination={{
          currentPage: page,
          totalPages,
          totalItems,
          itemsPerPage: PAGE_SIZE,
          startItem: (page - 1) * PAGE_SIZE + 1,
          endItem: Math.min(page * PAGE_SIZE, totalItems),
        }}
        onPageChange={handlePageChange}
        actions={actions}
        onExport={handleExport} // Add this line
        exportLoading={isExporting}
        actionsType="dropdown"
        filterPanel={
          <ConsumerOnboardingFilterPanel
            currentFilters={filters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
          />
        }
      />
    </>
  );
};
